import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { SelectBox } from "devextreme-react/select-box";

import { PageProps, UpdateDomTitleForForm } from "@ezhou/core";
import { MainCard } from "@ezhou/app_mantis_1_1";

import { UserOrganizationInfo, UserPreferenceKeyCurrentWorkingOrganization } from "../../model/userProfile";
import { getUserProfileFromCache } from "../../services/userSettingService";

const UserSettings = (pageProps: PageProps) => {
  const logger = pageProps.loggerProvider.getLogger("UserSettings");
  logger.debug("Rendering UserSettings component");

  const userProfile = useMemo(() => getUserProfileFromCache(pageProps.dataStore), [pageProps.dataStore]);
  const organizations = useMemo(() => userProfile.organizations || [], [userProfile.organizations]);
  
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number>(
    userProfile.defaultOrganization
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    UpdateDomTitleForForm(pageProps);
  });

  const handleOrganizationChange = useCallback((e: { value?: number }) => {
    if (e.value !== undefined) {
      setSelectedOrganizationId(e.value);
    }
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const httpClient = pageProps.appUtil.httpClients["eAccountingService"];
      await httpClient.post(
        "user-preferences/save",
        {
          key: UserPreferenceKeyCurrentWorkingOrganization,
          value: selectedOrganizationId.toString(),
        },
        (err) => {
          logger.error("Failed to save user preference", err);
        }
      );
      logger.info("User preference saved successfully");
      // Reload the page to apply the new organization setting
      window.location.reload();
    } catch (error) {
      logger.error("Error saving user preference", error);
    } finally {
      setIsSaving(false);
    }
  }, [selectedOrganizationId, pageProps.appUtil.httpClients, logger]);

  return (
    <MainCard title={pageProps.appUtil.getLocaleString("userSettings")}>
      <Box sx={{ maxWidth: 400 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {pageProps.appUtil.getLocaleString("currentWorkingOrganization")}
        </Typography>
        <SelectBox
          dataSource={organizations}
          displayExpr="fullName"
          valueExpr="organizationId"
          value={selectedOrganizationId}
          onValueChanged={handleOrganizationChange}
          searchEnabled={true}
          placeholder="Select an organization"
          width="100%"
        />
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaving || selectedOrganizationId === userProfile.defaultOrganization}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </MainCard>
  );
};

export default UserSettings;
